import { RoleName } from "@/api/users-types";
import { ApiErrorAlert } from "@/components/ui/ApiErrorAlert";
import { useHasRole } from "@/hooks/use-has-role";
import { useDeletePromptById } from "@/queries/prompts/use-delete-prompt-by-id";
import { usePromptById } from "@/queries/prompts/use-prompt-by-id";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useState } from "react";

export function DeletePrompt({ id }: { id: string }) {
  const [open, setOpen] = useState(false);

  const canDelete = useHasRole(RoleName.promptEngineer);

  if (!canDelete) {
    return null;
  }

  return (
    <>
      <DeletePromptButton onClick={() => setOpen(true)} />
      <DeletePromptDialog open={open} id={id} onClose={() => setOpen(false)} />
    </>
  );
}

function DeletePromptButton({ onClick }: { onClick: () => void }) {
  return (
    <>
      <Button color="error" variant={"outlined"} onClick={onClick}>
        Delete prompt
      </Button>
    </>
  );
}

function DeletePromptDialog({
  open,
  id,
  onClose,
}: {
  open: boolean;
  id: string;
  onClose: () => void;
}) {
  const { mutate: deletePrompt, isPending, error } = useDeletePromptById();

  const { data: prompt } = usePromptById(id);

  const handleClose = () => {
    onClose();
  };

  const handleDelete = () => {
    deletePrompt(
      { promptId: id },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Delete Prompt</DialogTitle>
      <DialogContent>
        {error && <ApiErrorAlert error={error} />}
        <Typography>Are you sure you want to delete this prompt?</Typography>
        {prompt && (
          <>
            <Typography color="textSecondary" mt={2}>
              {prompt.name}
            </Typography>
            <Typography color="textSecondary">{`Story type: ${prompt.storyType}`}</Typography>
            <Typography color="textSecondary">{`Prompt type: ${prompt.type}`}</Typography>
            <Typography color="textSecondary">{`Description: ${prompt.description}`}</Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          loading={isPending}
          color="error"
          variant={"contained"}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}
